import React, { useCallback, useEffect, useState } from 'react';
import { Coins, RefreshCw, AlertTriangle, Activity, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Button } from './ui/button';

const FIELDS = [
  { key: 'max_inflight', label: 'Generaciones simultáneas máximas', min: 1, max: 2000, help: 'Trabajos enviados a Genlook a la vez.' },
  { key: 'max_queued', label: 'Máximo de trabajos en cola', min: 1, max: 20000, help: 'Al llegar al límite, las nuevas solicitudes se rechazan sin cobrar crédito.' },
  { key: 'low_balance_threshold', label: 'Umbral de alerta de saldo Genlook', min: 0, max: 1000000, help: 'Solo genera una alerta; no bloquea el servicio.' },
  { key: 'signed_url_ttl_seconds', label: 'Duración de enlaces privados (s)', min: 60, max: 86400, help: 'Validez de las URLs firmadas de imágenes VTON.' },
];

const Stat = ({ label, value, testId, accent = 'text-white' }) => (
  <div className="bg-gray-800/60 rounded-lg p-4">
    <p className={`text-3xl font-bold ${accent}`} data-testid={testId}>{value ?? '—'}</p>
    <p className="text-gray-400 text-sm mt-1">{label}</p>
  </div>
);

export const AdminVtonPanel = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/vton/status');
      setData(res.data);
      setForm(Object.fromEntries(FIELDS.map(f => [f.key, String(res.data[f.key])])));
    } catch { toast.error('No se pudo cargar el estado de Genlook'); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const refreshBalance = async () => {
    setRefreshing(true);
    try {
      const res = await api.post('/admin/vton/balance/refresh');
      if (res.data.error) toast.error('Genlook no respondió; se mantiene el último saldo válido');
      else toast.success(`Saldo actualizado: ${res.data.credit_balance} créditos`);
      await load();
    } finally { setRefreshing(false); }
  };

  const saveSettings = async () => {
    const body = {};
    for (const f of FIELDS) {
      const v = Number(form[f.key]);
      if (!Number.isInteger(v) || v < f.min || v > f.max) { toast.error(`${f.label}: entre ${f.min} y ${f.max}`); return; }
      body[f.key] = v;
    }
    setSaving(true);
    try {
      await api.put('/admin/vton/settings', body);
      toast.success('Configuración del probador actualizada');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar'); } finally { setSaving(false); }
  };

  if (!data) return <div className="text-gray-400 py-10 text-center" data-testid="vton-admin-loading">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="vton-admin-panel">
      {data.alerts.length > 0 && (
        <div className="bg-amber-900/30 border border-amber-700 rounded-xl p-4 space-y-1" data-testid="vton-admin-alerts">
          {data.alerts.map((a) => (
            <p key={a} className="text-amber-300 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{a}</p>
          ))}
        </div>
      )}

      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Coins className="w-6 h-6 text-amber-400" />
            <h3 className="text-lg font-semibold text-white">Genlook — créditos y capacidad</h3>
          </div>
          <Button size="sm" variant="outline" onClick={refreshBalance} disabled={refreshing}
            className="border-gray-700 text-gray-300" data-testid="vton-refresh-balance-btn">
            {refreshing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Consultar saldo
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Créditos Genlook" value={data.credit_balance} testId="vton-credit-balance" accent="text-amber-400" />
          <Stat label={`En curso (máx ${data.max_inflight})`} value={data.inflight} testId="vton-inflight" />
          <Stat label={`En cola (máx ${data.max_queued})`} value={data.queued} testId="vton-queued" />
          <Stat label="429 en 24 h" value={data.rate_limited_24h} testId="vton-429-24h" />
        </div>
        <p className="text-xs text-gray-500 mt-4" data-testid="vton-balance-checked-at">
          {data.checked_at ? `Último saldo válido: ${new Date(data.checked_at).toLocaleString('es-PE')} (se actualiza cada hora)` : 'Saldo aún no consultado.'}
        </p>
      </div>

      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-testid="vton-settings-form">
        <h3 className="text-white font-semibold mb-4">Configuración del probador</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {FIELDS.map(f => (
            <label key={f.key} className="block">
              <span className="text-gray-300 text-sm">{f.label}</span>
              <input type="number" min={f.min} max={f.max} value={form[f.key] ?? ''}
                onChange={(e) => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                className="mt-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white w-full"
                data-testid={`vton-setting-${f.key.replace(/_/g, '-')}`} />
              <span className="text-gray-500 text-xs">{f.help}</span>
            </label>
          ))}
        </div>
        <Button onClick={saveSettings} disabled={saving} className="mt-4 bg-[#FF6B35] hover:bg-[#ff5a1f]" data-testid="vton-settings-save">
          <Save className="w-4 h-4 mr-1" /> Guardar
        </Button>
      </div>

      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
        <div className="flex items-center gap-2 mb-3"><Activity className="w-5 h-5 text-cyan-400" /><h3 className="text-white font-semibold">Eventos recientes</h3></div>
        {data.events.length === 0 ? <p className="text-gray-500 text-sm">Sin incidencias.</p> : (
          <ul className="space-y-1 text-sm" data-testid="vton-events-list">
            {data.events.map((e, i) => (
              <li key={i} className="text-gray-300 flex justify-between gap-4">
                <span className="font-mono">{e.type}{e.job_id ? ` · ${e.job_id.slice(0, 8)}` : ''}</span>
                <span className="text-gray-500">{new Date(e.created_at).toLocaleString('es-PE')}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminVtonPanel;
